firebird.factory('vdiService', [
    '$rootScope',
    '$timeout',
    '$interval',
    'meetingService',
    'helperService',
    '$window',
    'executionEnvironmentEnum',
    function ($rootScope, $timeout, $interval, meetingService, helperService, $window, executionEnvironmentEnum) {
        var annotationButtonBounds = null;
        var headerIconClipRects = [];
        var footerControlsBounds = null;
        var clipRectObserver = null;
        var clipRectObserverDebounce = null;
        var openPopupClipRects = [];
        var popupClipModeEnabled = false;
        var cancelTopBottomControlsWait = null;
        // True only once the header/footer show transition has fully settled - guards against
        // re-measuring header icon rects from a MutationObserver or window-resize refresh while they're
        // still mid-slide (hiding) or off-screen (hidden), which would carve clip holes at the wrong,
        // transient position and briefly crop/zoom the Horizon video overlay around them.
        var topBottomControlsSettledVisible = false;

        // CSS transition for the header/footer auto-hide slide is 300ms (see base.less) - used only as a
        // fallback below, for the rare case a transition gets interrupted and never fires transitionend.
        var TOP_BOTTOM_CONTROLS_TRANSITION_MS = 350;

        // adjustHeaderMenuPosition() (meetingActiveCtrl) mutates .header-inner's own class
        // (one-button-hidden) synchronously as part of *starting* a hide, milliseconds after the
        // 'showing-top-bottom-controls' broadcast - well before the real hide settles. A shorter debounce
        // here let that mutation's refresh fire first, while topBottomControlsSettledVisible was still
        // legitimately true (the settle hadn't happened yet), so the flag guard couldn't block it and it
        // re-added clip rects from mid-transition geometry (confirmed live via console instrumentation).
        // Keeping this comfortably longer than TOP_BOTTOM_CONTROLS_TRANSITION_MS guarantees the real settle
        // always runs first, so by the time this fires the flag guard correctly blocks it during a hide.
        var HEADER_ICON_MUTATION_DEBOUNCE_MS = TOP_BOTTOM_CONTROLS_TRANSITION_MS + 150;

        // dropdown-menus/popovers run a 250ms CSS transition on open (see dropdown.less); reading
        // getBoundingClientRect() before that settles can capture a mid-transition (e.g. scaled/offset)
        // rect - confirmed against the reactions picker, whose menu stayed covered even though a clip
        // rect was added, because the rect was measured too early.
        var POPUP_TRANSITION_MS = 300;

        // Device lists (mic/cam/speaker) keep growing after the dropdown opens - devices enumerate
        // asynchronously - so a single snapshot clips too small and truncates the newly-added rows. A
        // MutationObserver reacts instantly when that content changes; this interval is just a fallback
        // for size changes it wouldn't catch (e.g. a pure CSS-driven resize with no DOM mutation).
        var POPUP_RESIZE_POLL_MS = 500;

        // Debounce for the MutationObserver callback below, so a burst of DOM changes (e.g. a device
        // list populating row by row) doesn't re-clip on every single mutation.
        var POPUP_MUTATION_DEBOUNCE_MS = 50;

        // Shrinking the clip rect by a couple pixels on every side avoids a thin black border/seam along
        // its edge (confirmed via live testing) - the dropdown-menu/popover's own rounded corners
        // (dropdown.less: border-radius: 3px) leave a small gap past a perfectly square clip rect.
        var POPUP_CLIP_INSET_PX = 2;

        // '#mute-indicator-container' (the bottom "you're muted"/audio-disconnected bar) toggles via the
        // 'audio-muted'/'audio-disconnected' ng-class on #meeting-active (meeting-active.html), independent
        // of the header/footer auto-hide state, so it needs its own clip-rect tracking rather than reusing
        // the header/footer one above. CSS transition for it is 300ms (base.less) - used the same way as
        // TOP_BOTTOM_CONTROLS_TRANSITION_MS, as a fallback if transitionend never fires.
        var MUTE_INDICATOR_TRANSITION_MS = 350;
        var muteIndicatorClipRect = null;
        var muteIndicatorObserver = null;
        var muteIndicatorObservedElement = null;
        var muteIndicatorResizeObserver = null;
        var muteIndicatorObserverDebounce = null;
        var cancelMuteIndicatorWait = null;
        // #meeting-active/#mute-indicator-container can still be a detached (not-yet-appended) DOM fragment
        // the first time this runs from meetingActiveCtrl#init() - a single missed lookup here silently and
        // permanently disables mute-indicator clipping for the rest of the call, so retry like the nameplate
        // container below rather than give up after one try.
        var MUTE_INDICATOR_RETRY_MS = 500;
        var MUTE_INDICATOR_MAX_WAIT_MS = 20000;
        // The retry above's own $timeout handle - cancelled in disableMuteIndicatorClipMode.
        var muteIndicatorContainerRetryTimeout = null;

        // '#sidebar-toggle-button' ('Hide side panel') is small and never covered by the header/footer
        // auto-hide, so clipping it is permanent for the whole Horizon session (added once, never removed
        // while visible) rather than toggled. That also has a second effect confirmed via console
        // instrumentation: Horizon only visibly shifts/rescales the video when the active clip-region set
        // transitions to/from completely empty - keeping this one clip rect always present means the
        // header/footer auto-hide clip add/remove never drops the total to zero.
        var sidePanelToggleClipRect = null;
        var sidePanelToggleObserver = null;
        var sidePanelToggleResizeObserver = null;
        // #sidebar-left slides via a 300ms `left` transition (sidebar-left.less) when its 'toggled' class
        // flips; the button rides along inside it without changing size or its own container's
        // attributes, so neither observer above fires. Watch the aside's class and re-measure once the
        // slide settles.
        var sidePanelToggleSidebarObserver = null;
        var cancelSidePanelToggleWait = null;
        var SIDEBAR_TOGGLE_TRANSITION_MS = 350;

        // #meetingContent (the header/footer/mute-indicator's positioning ancestor) resizes whenever the
        // left sidebar is collapsed/expanded (the toggle button above) or dragged via its own resize
        // handle - neither fires a real window resize or the 'window-is-resized' broadcast that
        // windowIsResized() below listens for, so those layout changes would otherwise leave header/footer
        // clip rects at stale positions. A ResizeObserver catches any inner layout resize uniformly instead
        // of chasing every possible trigger individually.
        var layoutResizeObserver = null;

        // Each video tile has its own '.name-plate' (attendee display name), a real position:absolute
        // overlay (videoroom-client.less) shown by default on every tile, not just on hover - same
        // occlusion problem as the header/footer/mute-indicator, just per-tile and far more dynamic (tiles
        // are created/destroyed/repositioned constantly as the layout reflows, participants join/leave, or
        // active speaker changes). Tracked as a Map of element -> its currently-clipped rect so a refresh
        // only touches (remove+add) entries that actually moved/resized/appeared/disappeared, instead of
        // re-clipping every tile on every refresh - that dedup is what keeps this safe to run often without
        // reintroducing the add/remove churn that caused the video shift/zoom fixed elsewhere in this file.
        var nameplateClipEntries = new Map();
        var nameplateObserver = null;
        var nameplateResizeObserver = null;
        var nameplateRefreshDebounce = null;
        var nameplatePollInterval = null;
        var NAMEPLATE_REFRESH_DEBOUNCE_MS = 200;
        // Fallback for layout reflows (e.g. grid re-flow on participant count change) that resize/reposition
        // tiles without necessarily mutating any watched attribute - same reasoning as POPUP_RESIZE_POLL_MS.
        var NAMEPLATE_POLL_MS = 1000;
        // '#videoroom-videos' can still not exist when this is first called (videoroom-client's data-ng-if
        // depends on feed type, which may not have resolved yet at meetingActiveCtrl init()) - retry rather
        // than silently never clipping any name-plate for the rest of the meeting.
        var NAMEPLATE_CONTAINER_RETRY_MS = 500;
        var NAMEPLATE_CONTAINER_MAX_WAIT_MS = 20000;
        // The retry above's own $timeout handle - cancelled in disableVideoNameplateClipMode.
        var nameplateContainerRetryTimeout = null;
        // Above this many tiles, per-tile clipping stops being worth it: measuring every tile's rect on
        // each refresh gets more expensive, and a grid reflow (join/leave, layout change) touches many
        // tiles' rects at once - many simultaneous native add/remove calls is exactly what caused the
        // header/footer video shift/zoom fixed elsewhere in this file, just multiplied by tile count
        // instead of 2. Large grids also show tiles too small for the name to be as critical. Skip clipping
        // entirely above this threshold rather than accept that risk for a many-tile layout.
        var NAMEPLATE_MAX_TILES = 8;

        // '#reactions-animation.vdi-stationary' is the fixed tray reactionsAnimationCtrl switches to when
        // VDIRef.showAnimatedOverlaysOverVideo is false (ANYAM-123367): reactions and remote raised-hand
        // emojis animate in place inside it instead of crossing the stage, so one clip rect for the tray -
        // added when the first item appears, removed once the last one is gone - is enough. The controller
        // drives that via the 'reactions-tray-visible' broadcast; the flag is remembered even while the mode
        // is off so a tray that was already showing gets clipped the moment video appears.
        var reactionsTrayClipRect = null;
        var reactionsTrayVisible = false;
        var reactionsTrayClipModeEnabled = false;

        // popup/mute-indicator/side-panel-toggle/layout-resize/nameplate clipping all exist solely to
        // punch holes for HTML that Omnissa's native video overlay would otherwise paint over - with zero
        // active video renders there's no overlay to hide behind, so running any of it (MutationObservers,
        // ResizeObservers, $interval polling) is pure overhead for an audio-only call. Tracks whether
        // that group is currently on so onVideoRoomWebcamsVisible only (en/dis)ables it once per actual
        // transition, as tiles turn video on/off through the meeting.
        var videoDependentClipModesEnabled = false;
        // Guards onVideoRoomWebcamsVisible so the broadcast (which fires for the whole app lifetime) is
        // only acted on while a Horizon meeting has actually asked for this via enableClipModesForVideoPresence.
        var clipModesForVideoPresenceStarted = false;
        // windowIsResized's refresh of the header/footer clip rects - cancelled in disableAllClipModes.
        var windowResizeRefreshTimeout = null;

        $rootScope.$on('vdi-toggle-annotation-ui', onVdiToggleAnnotationUi);
        $rootScope.$on('vdi-reshow-annotation-ui', onVdiReshowAnnotationUi);
        $rootScope.$on('window-is-resized', windowIsResized);
        $rootScope.$on('showing-top-bottom-controls', onShowingTopBottomControls);
        // videoLayoutService/videoLayoutLockService already broadcast this on every layout recalculation
        // (join/leave, camera on/off, layout change) - reuse it instead of watching '#videoroom-videos'
        // ourselves with a second MutationObserver.
        $rootScope.$on('video-room-webcams-visible', onVideoRoomWebcamsVisible);
        $rootScope.$on('reactions-tray-visible', onReactionsTrayVisible);

        var service = {
            setAnnotationButtonBounds: setAnnotationButtonBounds,
            onVdiToggleAnnotationUi: onVdiToggleAnnotationUi,
            isHorizonVdi: isHorizonVdi,
            enableHeaderFooterClipMode: refreshTopBottomControlsClipRects,
            disableHeaderFooterClipMode: removeTopBottomControlsClipRects,
            enablePopupClipMode: enablePopupClipMode,
            disablePopupClipMode: disablePopupClipMode,
            enableMuteIndicatorClipMode: enableMuteIndicatorClipMode,
            disableMuteIndicatorClipMode: disableMuteIndicatorClipMode,
            enableSidePanelToggleClipMode: enableSidePanelToggleClipMode,
            disableSidePanelToggleClipMode: disableSidePanelToggleClipMode,
            enableLayoutResizeClipSync: enableLayoutResizeClipSync,
            disableLayoutResizeClipSync: disableLayoutResizeClipSync,
            enableVideoNameplateClipMode: enableVideoNameplateClipMode,
            disableVideoNameplateClipMode: disableVideoNameplateClipMode,
            enableReactionsTrayClipMode: enableReactionsTrayClipMode,
            disableReactionsTrayClipMode: disableReactionsTrayClipMode,
            // Starts/stops popup/mute-indicator/side-panel-toggle/layout-resize/nameplate clipping as a
            // group, dynamically, based on whether any video tile is actually rendering - see
            // refreshClipModesForVideoPresence for why. Replaces calling the five enable*ClipMode above
            // individually and unconditionally for the whole meeting.
            enableClipModesForVideoPresence: enableClipModesForVideoPresence,
            // Single teardown call for meetingActiveCtrl's $destroy - vdiService is an app-lifetime
            // singleton, so every enable*ClipMode above (document-level Bootstrap handlers, Mutation/
            // ResizeObservers, $interval polling) would otherwise keep running against a torn-down meeting
            // DOM across meeting navigation, wasting CPU and risking stale clip regions.
            disableAllClipModes: disableAllClipModes
        };

        return service;

        // helperService.getVDIRef() is undefined until firebirdCtrl's async VDI-type detection resolves
        // (setVDIRef), so anything reachable from a $rootScope broadcast/timer before then - e.g. an early
        // 'window-is-resized' - must not assume it's set. Route every native-SDK call through
        // addClipRectSafe/removeClipRectSafe below instead of calling helperService.getVDIRef() directly.
        function isHorizonVdi() {
            var vdiRef = helperService.getVDIRef();
            return !!vdiRef && vdiRef.getVdiType() === executionEnvironmentEnum.HorizonOmnissa;
        }

        function addClipRectSafe(rect) {
            var vdiRef = helperService.getVDIRef();
            if (vdiRef && rect) {
                vdiRef.addClipRect(rect);
            }
        }

        function removeClipRectSafe(rect) {
            var vdiRef = helperService.getVDIRef();
            if (vdiRef && rect) {
                vdiRef.removeClipRect(rect);
            }
        }

        // DOMRect's fields are prototype accessors, not own enumerable properties - logging a raw rect
        // often prints as an empty object depending on the console/serializer.
        function rectToPlain(rect) {
            return {
                top: rect.top,
                left: rect.left,
                right: rect.right,
                bottom: rect.bottom,
                width: rect.width,
                height: rect.height
            };
        }

        function windowIsResized() {
            if (annotationButtonBounds) {
                $timeout(() => {
                    addClipRectSafe(annotationButtonBounds);
                }, 500);
            }
            if (isHorizonVdi() && topBottomControlsSettledVisible && videoDependentClipModesEnabled) {
                // Debounce - a resize burst would otherwise stack overlapping refreshes.
                $timeout.cancel(windowResizeRefreshTimeout);
                windowResizeRefreshTimeout = $timeout(
                    refreshTopBottomControlsClipRects,
                    TOP_BOTTOM_CONTROLS_TRANSITION_MS
                );
            }
            if (reactionsTrayClipRect) {
                syncReactionsTrayClipRect();
            }
        }

        // Omnissa renders redirected video as a native overlay that ignores CSS z-index, so the header/
        // footer controls (mute, cam, leave) become unclickable whenever a video tile's on-screen bounds
        // extend under them. Carve out clip rects for both whenever they're shown, in sync with the
        // 'showing-top-bottom-controls' broadcast (meetingActiveCtrl) that drives the auto-hide CSS
        // transition, and remove them once the controls auto-hide so the video can render there instead -
        // Horizon uses the same mouse-driven auto-hide as non-VDI (meetingActiveCtrl#init), so this is the
        // only place clip mode gets enabled/disabled; nothing calls enableHeaderFooterClipMode directly.
        //
        // Both directions wait for the real CSS transition to finish (not a fixed guess) before touching
        // the clip rects, so the update lands exactly when the header/footer visually settles rather than
        // slightly early (mid-fade) or late (a visible gap after it's already done). Any pending wait is
        // cancelled - without settling - if the state flips again first, otherwise a quick re-show right
        // after a hide could let the stale removal fire after the re-add, flashing the video over an
        // already-visible header for the gap between the two (confirmed by tracing the timers).
        function onShowingTopBottomControls(event, isShowing) {
            if (!isHorizonVdi()) {
                return;
            }
            if (cancelTopBottomControlsWait) {
                cancelTopBottomControlsWait();
            }
            cancelTopBottomControlsWait = waitForTopBottomControlsTransition(function () {
                cancelTopBottomControlsWait = null;
                topBottomControlsSettledVisible = isShowing;
                syncTopBottomControlsClipRects();
            });
        }

        // Same "no active video render means no native overlay to hide behind" reasoning as
        // refreshClipModesForVideoPresence - only actually carve the header/footer clip rects when both
        // the controls are shown AND there's video to hide them behind.
        function syncTopBottomControlsClipRects() {
            if (topBottomControlsSettledVisible && videoDependentClipModesEnabled) {
                refreshTopBottomControlsClipRects();
            } else {
                removeTopBottomControlsClipRects();
            }
        }

        // #header is the ancestor of both .header-inner (slides via 'right') and .footer-menu/
        // .compact-mode-footer-menu (slides via 'bottom' + fades via 'opacity'), so one bubble-phase
        // listener on it catches whichever settles - filtered to those specific elements so unrelated
        // bubbled transitions inside the header (icon hover, tooltips) can't trigger an early, mid-fade
        // measurement. The fallback timeout guarantees this always resolves even if transitionend never
        // fires - e.g. the transition gets interrupted by another toggle before completing.
        function waitForTopBottomControlsTransition(onSettled) {
            var headerElement = $window.$('#header').get(0);
            var fallbackTimeout = $timeout(settle, TOP_BOTTOM_CONTROLS_TRANSITION_MS);

            if (headerElement) {
                headerElement.addEventListener('transitionend', onTransitionEnd);
            }

            return cancel;

            function onTransitionEnd(event) {
                var target = event.target;
                var isRelevantTarget =
                    target &&
                    target.classList &&
                    (target.classList.contains('header-inner') ||
                        target.classList.contains('footer-menu') ||
                        target.classList.contains('compact-mode-footer-menu'));
                if (isRelevantTarget) {
                    settle();
                }
            }

            function cancel() {
                $timeout.cancel(fallbackTimeout);
                if (headerElement) {
                    headerElement.removeEventListener('transitionend', onTransitionEnd);
                }
            }

            function settle() {
                cancel();
                onSettled();
            }
        }

        function refreshTopBottomControlsClipRects() {
            if (!topBottomControlsSettledVisible) {
                // The MutationObserver below only checks this at the moment a mutation fires, not when
                // its debounced $timeout actually runs 300ms later - a mutation firing just before a hide
                // settles can still schedule this to run just after, re-adding clip rects at the
                // still-shown position and confirmed (via console instrumentation) to immediately get
                // removed again a few ms later, producing exactly the extra add/remove churn around every
                // hide that Horizon visibly reflows/rescales for. Re-check here as the actual guard.
                return;
            }
            removeTopBottomControlsClipRects();

            // #header itself is a full-content-height, full-width wrapper (confirmed via devtools: its
            // rect matched #background's, ~1000px tall) with mostly transparent space. A single bounding
            // rect around the icon cluster (rather than one clip call per icon) means show/hide only ever
            // costs one native setVideoClipRegion call for the header - Horizon visibly reflows/rescales
            // the video on every individual add/remove call, so the previous per-icon approach (up to ~7
            // calls) produced a multi-step shift/zoom flicker on every show/hide; this trades away letting
            // video show through the small transparent gaps between icons, which isn't visible in practice.
            var headerInnerElement = $window.$('#header .header-inner').get(0);
            var iconElements = $window.$('#header .top-menu > li.menu-icon').get();
            var headerRect = unionVisibleRects(iconElements);
            if (headerRect) {
                headerIconClipRects.push(headerRect);
                addClipRectSafe(headerRect);
            }

            var footerElement = $window.$('.footer-menu, .compact-mode-footer-menu').get(0);
            if (footerElement) {
                footerControlsBounds = footerElement.getBoundingClientRect();
                addClipRectSafe(footerControlsBounds);
            }

            watchForClipRectChanges(headerInnerElement, footerElement);
        }

        // Bounding rect around every visible element's own rect, or null if none are visible/sized.
        function unionVisibleRects(elements) {
            var union = null;
            elements.forEach(function (element) {
                var style = $window.getComputedStyle(element);
                if (style.display === 'none' || style.visibility === 'hidden') {
                    return;
                }
                var rect = element.getBoundingClientRect();
                if (rect.width <= 0 || rect.height <= 0) {
                    return;
                }
                if (!union) {
                    union = { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom };
                } else {
                    union.left = Math.min(union.left, rect.left);
                    union.top = Math.min(union.top, rect.top);
                    union.right = Math.max(union.right, rect.right);
                    union.bottom = Math.max(union.bottom, rect.bottom);
                }
            });
            if (!union) {
                return null;
            }
            var rect = new DOMRect();
            rect.x = union.left;
            rect.y = union.top;
            rect.width = union.right - union.left;
            rect.height = union.bottom - union.top;
            return rect;
        }

        // Icons within the header/footer clusters come and go based on meeting state (recording toggle,
        // connectivity icon, security toggle, compact mode, etc.) via Angular ng-if/ng-show, which changes
        // real layout without firing a window resize. Watch both clusters directly so the clip rect stays
        // accurate to whatever's actually visible, not just on resize.
        function watchForClipRectChanges(headerInnerElement, footerElement) {
            if (clipRectObserver || typeof MutationObserver === 'undefined') {
                return;
            }
            clipRectObserver = new MutationObserver(function () {
                if (!topBottomControlsSettledVisible) {
                    // Controls are hiding/hidden - an unrelated mutation (e.g. recording icon toggling)
                    // firing mid-transition must not re-measure/re-add clip rects from stale, in-flight
                    // geometry. Once shown again, refreshTopBottomControlsClipRects() re-measures anyway.
                    return;
                }
                $timeout.cancel(clipRectObserverDebounce);
                clipRectObserverDebounce = $timeout(
                    refreshTopBottomControlsClipRects,
                    HEADER_ICON_MUTATION_DEBOUNCE_MS
                );
            });
            var observerOptions = {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['style', 'class']
            };
            if (headerInnerElement) {
                clipRectObserver.observe(headerInnerElement, observerOptions);
            }
            if (footerElement) {
                clipRectObserver.observe(footerElement, observerOptions);
            }
        }

        function removeTopBottomControlsClipRects() {
            // Not touching topBottomControlsSettledVisible here - refreshTopBottomControlsClipRects()
            // calls this first to clear stale rects even on the show path, which would otherwise clobber
            // the true it just set; onShowingTopBottomControls is the only place that flips the flag.
            if (clipRectObserver) {
                clipRectObserver.disconnect();
                clipRectObserver = null;
            }
            $timeout.cancel(clipRectObserverDebounce);
            headerIconClipRects.forEach(function (rect) {
                removeClipRectSafe(rect);
            });
            headerIconClipRects = [];
            if (footerControlsBounds) {
                removeClipRectSafe(footerControlsBounds);
                footerControlsBounds = null;
            }
        }

        // Watches #meeting-active's class attribute (rather than the indicator element itself) because
        // ng-class toggles 'audio-muted'/'audio-disconnected' there, not on #mute-indicator-container.
        // Re-callable: if #meeting-active was re-rendered (a fresh DOM node) since the last call, the old
        // observer is watching a detached element and is torn down/rebuilt against the current one instead
        // of being skipped, so mute-indicator clipping doesn't silently stop working after that happens.
        function enableMuteIndicatorClipMode(waitedMs) {
            var meetingActiveElement = $window.$('#meeting-active').get(0);
            var indicatorElement = $window.$('#mute-indicator-container').get(0);
            if (!meetingActiveElement || !indicatorElement) {
                waitedMs = waitedMs || 0;
                if (waitedMs >= MUTE_INDICATOR_MAX_WAIT_MS) {
                    return;
                }
                muteIndicatorContainerRetryTimeout = $timeout(function () {
                    enableMuteIndicatorClipMode(waitedMs + MUTE_INDICATOR_RETRY_MS);
                }, MUTE_INDICATOR_RETRY_MS);
                return;
            }
            if (muteIndicatorObserver) {
                if (muteIndicatorObservedElement === meetingActiveElement) {
                    return;
                }
                muteIndicatorObserver.disconnect();
                muteIndicatorObserver = null;
            }
            if (muteIndicatorResizeObserver) {
                muteIndicatorResizeObserver.disconnect();
                muteIndicatorResizeObserver = null;
            }
            muteIndicatorObservedElement = meetingActiveElement;

            if (typeof MutationObserver !== 'undefined') {
                // ng-show also toggles 'ng-hide' directly on #mute-indicator-container itself (independent
                // of the 'audio-muted'/'audio-disconnected' ancestor classes above) - watching only
                // #meeting-active missed that path entirely, so the indicator could become visible/hidden
                // with no clip-rect update at all.
                muteIndicatorObserver = new MutationObserver(function () {
                    $timeout.cancel(muteIndicatorObserverDebounce);
                    muteIndicatorObserverDebounce = $timeout(onMuteIndicatorVisibilityMayHaveChanged, 50);
                });
                muteIndicatorObserver.observe(meetingActiveElement, { attributes: true, attributeFilter: ['class'] });
                muteIndicatorObserver.observe(indicatorElement, {
                    attributes: true,
                    attributeFilter: ['class', 'style']
                });
            }

            if (typeof ResizeObserver !== 'undefined') {
                // Its width is 100% of #meetingContent, which resizes on sidebar toggle/feature-flags-panel
                // open without firing 'window-is-resized' or mutating any class we watch above - a stale
                // rect from before that resize would clip too narrow (or too wide) relative to the bar's
                // actual current width. ResizeObserver catches the resize directly, regardless of cause.
                muteIndicatorResizeObserver = new ResizeObserver(function () {
                    if (muteIndicatorClipRect) {
                        addMuteIndicatorClipRect(indicatorElement);
                    }
                });
                muteIndicatorResizeObserver.observe(indicatorElement);
            }

            // Sync immediately (e.g. joined already muted) instead of via waitForElementTransition, since
            // nothing is actually transitioning yet at enable time.
            syncMuteIndicatorClipRect();
        }

        function onMuteIndicatorVisibilityMayHaveChanged() {
            var element = $window.$('#mute-indicator-container').get(0);
            // Don't pre-check current visibility here to skip a "no-op" mutation - the class mutation is
            // what *starts* the opacity/bottom transition, so computed style at this exact instant is
            // still the pre-change value almost every time, making that check always look like "no change"
            // and skip re-syncing entirely (confirmed live: the clip rect never got added at all). Always
            // wait for the real transition/settle below and let syncMuteIndicatorClipRect() - which reads
            // the actual state at that later point - decide; it's idempotent, so an extra call is harmless.
            if (cancelMuteIndicatorWait) {
                cancelMuteIndicatorWait();
            }
            cancelMuteIndicatorWait = waitForElementTransition(element, MUTE_INDICATOR_TRANSITION_MS, function () {
                cancelMuteIndicatorWait = null;
                syncMuteIndicatorClipRect();
            });
        }

        function syncMuteIndicatorClipRect() {
            var element = $window.$('#mute-indicator-container').get(0);
            var visible = isElementVisible(element);
            if (visible) {
                addMuteIndicatorClipRect(element);
            } else {
                removeMuteIndicatorClipRect();
            }
        }

        function isElementVisible(element) {
            if (!element) {
                return false;
            }
            var style = $window.getComputedStyle(element);
            return style.display !== 'none' && parseFloat(style.opacity) > 0;
        }

        function addMuteIndicatorClipRect(element) {
            removeMuteIndicatorClipRect();
            // #mute-indicator-container is 100% width (its ancestor's full width) and mostly transparent -
            // only #mute-indicator, the centered pill inside it, is actually visible/red. Clip that instead
            // of the whole wide container, so the punched hole isn't far wider than the bar itself.
            var pillElement = element.querySelector('#mute-indicator') || element;
            var rect = pillElement.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) {
                muteIndicatorClipRect = rect;
                addClipRectSafe(rect);
            }
        }

        function removeMuteIndicatorClipRect() {
            if (muteIndicatorClipRect) {
                removeClipRectSafe(muteIndicatorClipRect);
                muteIndicatorClipRect = null;
            }
        }

        function disableMuteIndicatorClipMode() {
            if (muteIndicatorObserver) {
                muteIndicatorObserver.disconnect();
                muteIndicatorObserver = null;
            }
            if (muteIndicatorResizeObserver) {
                muteIndicatorResizeObserver.disconnect();
                muteIndicatorResizeObserver = null;
            }
            $timeout.cancel(muteIndicatorObserverDebounce);
            $timeout.cancel(muteIndicatorContainerRetryTimeout);
            if (cancelMuteIndicatorWait) {
                cancelMuteIndicatorWait();
                cancelMuteIndicatorWait = null;
            }
            muteIndicatorObservedElement = null;
            removeMuteIndicatorClipRect();
        }

        function enableSidePanelToggleClipMode() {
            var element = $window.$('#sidebar-toggle-button').get(0);
            if (!element) {
                return;
            }
            syncSidePanelToggleClipRect(element);

            if (typeof ResizeObserver !== 'undefined' && !sidePanelToggleResizeObserver) {
                sidePanelToggleResizeObserver = new ResizeObserver(function () {
                    syncSidePanelToggleClipRect(element);
                });
                sidePanelToggleResizeObserver.observe(element);
            }

            // Its container's class toggles (duration-timer adjustment, compact mode) can reposition it
            // without a size change, which ResizeObserver alone wouldn't catch.
            var container = $window.$('#sidebar-toggle-button-container').get(0);
            if (container && typeof MutationObserver !== 'undefined' && !sidePanelToggleObserver) {
                sidePanelToggleObserver = new MutationObserver(function () {
                    syncSidePanelToggleClipRect(element);
                });
                sidePanelToggleObserver.observe(container, { attributes: true, attributeFilter: ['class', 'style'] });
            }

            var sidebar = $window.$('#sidebar-left').get(0);
            if (sidebar && typeof MutationObserver !== 'undefined' && !sidePanelToggleSidebarObserver) {
                // Dragging the sidebar's resize handle (.rg-right) moves the button with the right edge
                // without resizing it - the sidebar's own size change is the only signal.
                if (sidePanelToggleResizeObserver) {
                    sidePanelToggleResizeObserver.observe(sidebar);
                }
                sidePanelToggleSidebarObserver = new MutationObserver(function () {
                    if (cancelSidePanelToggleWait) {
                        cancelSidePanelToggleWait();
                    }
                    cancelSidePanelToggleWait = waitForElementTransition(
                        sidebar,
                        SIDEBAR_TOGGLE_TRANSITION_MS,
                        function () {
                            cancelSidePanelToggleWait = null;
                            syncSidePanelToggleClipRect(element);
                        }
                    );
                });
                sidePanelToggleSidebarObserver.observe(sidebar, { attributes: true, attributeFilter: ['class'] });
            }
        }

        function syncSidePanelToggleClipRect(element) {
            var rect = element.getBoundingClientRect();
            var isVisible = rect.width > 0 && rect.height > 0;
            if (sidePanelToggleClipRect) {
                removeClipRectSafe(sidePanelToggleClipRect);
                sidePanelToggleClipRect = null;
            }
            // Hidden in compact mode/no-sidebar layouts - nothing to protect, and keeping the stale rect
            // would incorrectly punch a hole where the button no longer is.
            if (isVisible) {
                addClipRectSafe(rect);
                sidePanelToggleClipRect = rect;
            }
        }

        function disableSidePanelToggleClipMode() {
            if (sidePanelToggleObserver) {
                sidePanelToggleObserver.disconnect();
                sidePanelToggleObserver = null;
            }
            if (sidePanelToggleResizeObserver) {
                sidePanelToggleResizeObserver.disconnect();
                sidePanelToggleResizeObserver = null;
            }
            if (sidePanelToggleSidebarObserver) {
                sidePanelToggleSidebarObserver.disconnect();
                sidePanelToggleSidebarObserver = null;
            }
            if (cancelSidePanelToggleWait) {
                cancelSidePanelToggleWait();
                cancelSidePanelToggleWait = null;
            }
            if (sidePanelToggleClipRect) {
                removeClipRectSafe(sidePanelToggleClipRect);
                sidePanelToggleClipRect = null;
            }
        }

        function enableLayoutResizeClipSync() {
            if (layoutResizeObserver || typeof ResizeObserver === 'undefined') {
                return;
            }
            var layoutElement = $window.$('#meetingContent').get(0);
            if (!layoutElement) {
                return;
            }
            layoutResizeObserver = new ResizeObserver(function () {
                // refreshTopBottomControlsClipRects() itself no-ops while hidden/mid-transition, so it's
                // safe to just always call it here rather than duplicating that check.
                if (isHorizonVdi()) {
                    refreshTopBottomControlsClipRects();
                }
                // The tray is anchored to #meetingContent's bottom-right, so it moves with the same resizes.
                if (reactionsTrayClipRect) {
                    syncReactionsTrayClipRect();
                }
            });
            layoutResizeObserver.observe(layoutElement);
        }

        function disableLayoutResizeClipSync() {
            if (layoutResizeObserver) {
                layoutResizeObserver.disconnect();
                layoutResizeObserver = null;
            }
        }

        // '#videoroom-videos' is the tile grid container - watching it (subtree) catches tiles being
        // added/removed/reordered, and attribute changes (talking/pinned/etc. toggle name-plate styling).
        function enableVideoNameplateClipMode(waitedMs) {
            if (nameplateObserver) {
                return;
            }
            var container = $window.$('#videoroom-videos').get(0);
            if (!container) {
                waitedMs = waitedMs || 0;
                if (waitedMs >= NAMEPLATE_CONTAINER_MAX_WAIT_MS) {
                    return;
                }
                nameplateContainerRetryTimeout = $timeout(function () {
                    enableVideoNameplateClipMode(waitedMs + NAMEPLATE_CONTAINER_RETRY_MS);
                }, NAMEPLATE_CONTAINER_RETRY_MS);
                return;
            }

            if (typeof MutationObserver !== 'undefined') {
                nameplateObserver = new MutationObserver(scheduleNameplateRefresh);
                nameplateObserver.observe(container, {
                    childList: true,
                    subtree: true,
                    attributes: true,
                    attributeFilter: ['class', 'style', 'displayname', 'videoLabels']
                });
            }
            if (typeof ResizeObserver !== 'undefined') {
                nameplateResizeObserver = new ResizeObserver(scheduleNameplateRefresh);
                nameplateResizeObserver.observe(container);
            }
            $interval.cancel(nameplatePollInterval);
            nameplatePollInterval = $interval(refreshVideoNameplateClipRects, NAMEPLATE_POLL_MS);

            refreshVideoNameplateClipRects();
        }

        function scheduleNameplateRefresh() {
            $timeout.cancel(nameplateRefreshDebounce);
            nameplateRefreshDebounce = $timeout(refreshVideoNameplateClipRects, NAMEPLATE_REFRESH_DEBOUNCE_MS);
        }

        function refreshVideoNameplateClipRects() {
            if (!isHorizonVdi()) {
                return;
            }
            var nameplateElements = $window
                .$('#videoroom-videos > div[enable="true"]:not(.audioonly) .name-plate')
                .get();
            if (nameplateElements.length > NAMEPLATE_MAX_TILES) {
                clearNameplateClipRects();
                return;
            }
            var stillPresent = new Set();

            nameplateElements.forEach(function (element) {
                var style = $window.getComputedStyle(element);
                if (style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity) <= 0) {
                    return;
                }
                var rect = element.getBoundingClientRect();
                if (rect.width <= 0 || rect.height <= 0) {
                    return;
                }
                stillPresent.add(element);
                var existingRect = nameplateClipEntries.get(element);
                if (existingRect && rectsEqual(existingRect, rect)) {
                    return;
                }
                if (existingRect) {
                    removeClipRectSafe(existingRect);
                }
                addClipRectSafe(rect);
                nameplateClipEntries.set(element, rect);
            });

            // Anything previously clipped that's now gone (tile removed) or hidden (compact-mode local
            // tile rule, etc.) needs its clip rect removed - otherwise it punches a permanent, unmatched
            // hole where a name-plate used to be.
            nameplateClipEntries.forEach(function (rect, element) {
                if (!stillPresent.has(element)) {
                    removeClipRectSafe(rect);
                    nameplateClipEntries.delete(element);
                }
            });
        }

        // Drops every currently-tracked name-plate clip rect (e.g. tile count just went over
        // NAMEPLATE_MAX_TILES) so none are left stale/unmanaged once refreshes stop touching them.
        function clearNameplateClipRects() {
            nameplateClipEntries.forEach(function (rect) {
                removeClipRectSafe(rect);
            });
            nameplateClipEntries.clear();
        }

        function disableVideoNameplateClipMode() {
            if (nameplateObserver) {
                nameplateObserver.disconnect();
                nameplateObserver = null;
            }
            if (nameplateResizeObserver) {
                nameplateResizeObserver.disconnect();
                nameplateResizeObserver = null;
            }
            $timeout.cancel(nameplateRefreshDebounce);
            $timeout.cancel(nameplateContainerRetryTimeout);
            $interval.cancel(nameplatePollInterval);
            nameplatePollInterval = null;
            clearNameplateClipRects();
        }

        function onReactionsTrayVisible(event, visible) {
            reactionsTrayVisible = !!visible;
            if (reactionsTrayClipModeEnabled) {
                syncReactionsTrayClipRect();
            }
        }

        function enableReactionsTrayClipMode() {
            reactionsTrayClipModeEnabled = true;
            syncReactionsTrayClipRect();
        }

        function syncReactionsTrayClipRect() {
            if (!reactionsTrayClipModeEnabled || !isHorizonVdi()) {
                return;
            }
            var element = $window.$('#reactions-animation.vdi-stationary').get(0);
            // Not isElementVisible(): the pill fades in via an opacity transition, so its computed opacity
            // is still 0 at the instant the controller broadcasts (confirmed on VDI: clip never added).
            // The controller's broadcast is the visibility signal; only a display:none layout counts as hidden.
            if (!reactionsTrayVisible || !element || $window.getComputedStyle(element).display === 'none') {
                removeReactionsTrayClipRect();
                return;
            }
            // No inset here (unlike popups): the pill is black like the hole behind it, so its full rect
            // (border included) must be clipped for the rounded outline to show.
            var rect = element.getBoundingClientRect();
            if (rect.width <= 0 || rect.height <= 0) {
                removeReactionsTrayClipRect();
                return;
            }
            if (reactionsTrayClipRect && rectsEqual(reactionsTrayClipRect, rect)) {
                return;
            }
            removeReactionsTrayClipRect();
            reactionsTrayClipRect = rect;
            addClipRectSafe(rect);
        }

        function removeReactionsTrayClipRect() {
            if (reactionsTrayClipRect) {
                removeClipRectSafe(reactionsTrayClipRect);
                reactionsTrayClipRect = null;
            }
        }

        function disableReactionsTrayClipMode() {
            reactionsTrayClipModeEnabled = false;
            removeReactionsTrayClipRect();
        }

        // Single teardown for every clip mode above - see disableAllClipModes export comment.
        function disableAllClipModes() {
            if (cancelTopBottomControlsWait) {
                cancelTopBottomControlsWait();
                cancelTopBottomControlsWait = null;
            }
            removeTopBottomControlsClipRects();
            // vdiService is a singleton that can outlive a single meeting (navigation without a page
            // reload) - a stale true/false here would make the next Horizon meeting's first header/footer
            // toggle misbehave via refreshTopBottomControlsClipRects()'s guard, until a broadcast fixes it.
            topBottomControlsSettledVisible = false;
            $timeout.cancel(windowResizeRefreshTimeout);
            disableVideoPresenceWatcher();
            disablePopupClipMode();
            disableMuteIndicatorClipMode();
            disableSidePanelToggleClipMode();
            disableLayoutResizeClipSync();
            disableVideoNameplateClipMode();
            disableReactionsTrayClipMode();
            // A new meeting's tray starts empty; reactionsAnimationCtrl re-broadcasts on the first reaction.
            reactionsTrayVisible = false;
            // Cancels any add/removeClipRect retry still waiting on the Horizon API/windowRef, so it
            // can't fire against a torn-down meeting - see HorizonSdkService#resetClipState.
            var vdiRef = helperService.getVDIRef();
            if (vdiRef && vdiRef.resetClipState) {
                vdiRef.resetClipState();
            }
        }

        // '#videoroom-videos > div[enable="true"]:not(.audioonly)' is the same selector already used
        // elsewhere in the app (e.g. videoFeatureFlagsService) to mean "at least one video tile is
        // actually rendering video", as opposed to an audio-only placeholder tile. Used only for the
        // synchronous initial check below - 'video-room-webcams-visible' covers every change after that.
        function hasActiveVideoRender() {
            return $window.$('#videoroom-videos > div[enable="true"]:not(.audioonly)').length > 0;
        }

        // Starts reacting to 'video-room-webcams-visible' (broadcast by videoLayoutService/
        // videoLayoutLockService on every layout recalculation) to (en/dis)able the video-dependent clip
        // modes together as video comes and goes, instead of leaving them all running for the whole
        // meeting - most Horizon calls start audio-only. Checks the current state immediately too, in
        // case the broadcast already fired before this was called.
        function enableClipModesForVideoPresence() {
            if (clipModesForVideoPresenceStarted) {
                return;
            }
            clipModesForVideoPresenceStarted = true;
            refreshClipModesForVideoPresence(hasActiveVideoRender());
        }

        function onVideoRoomWebcamsVisible(event, visible) {
            if (!clipModesForVideoPresenceStarted) {
                return;
            }
            // videoLayoutService reports visible=false for a self-PIP-only tile even though it's still
            // rendered, so recompute from the DOM here instead of trusting the broadcast value.
            refreshClipModesForVideoPresence(hasActiveVideoRender());
        }

        function refreshClipModesForVideoPresence(hasVideo) {
            if (hasVideo === videoDependentClipModesEnabled) {
                return;
            }
            videoDependentClipModesEnabled = hasVideo;
            if (hasVideo) {
                enablePopupClipMode();
                enableMuteIndicatorClipMode();
                enableSidePanelToggleClipMode();
                enableLayoutResizeClipSync();
                enableVideoNameplateClipMode();
                enableReactionsTrayClipMode();
            } else {
                disablePopupClipMode();
                disableMuteIndicatorClipMode();
                disableSidePanelToggleClipMode();
                disableLayoutResizeClipSync();
                disableVideoNameplateClipMode();
                disableReactionsTrayClipMode();
            }
            syncTopBottomControlsClipRects();
        }

        // Teardown counterpart for enableClipModesForVideoPresence - see disableAllClipModes.
        function disableVideoPresenceWatcher() {
            clipModesForVideoPresenceStarted = false;
            videoDependentClipModesEnabled = false;
        }

        // Generic version of waitForTopBottomControlsTransition above, for a single element instead of
        // the header's two independently-transitioning children.
        function waitForElementTransition(element, fallbackMs, onSettled) {
            var fallbackTimeout = $timeout(settle, fallbackMs);
            if (element) {
                element.addEventListener('transitionend', onTransitionEnd);
            }

            return cancel;

            function onTransitionEnd(event) {
                if (event.target === element) {
                    settle();
                }
            }

            function cancel() {
                $timeout.cancel(fallbackTimeout);
                if (element) {
                    element.removeEventListener('transitionend', onTransitionEnd);
                }
            }

            function settle() {
                cancel();
                onSettled();
            }
        }

        // Device-list dropdowns (mic/cam/speaker/screenshare), the reactions/hand-raise picker,
        // popovers (muted-mic, mic-unplugged, etc.), and tooltips (every header icon uses
        // data-container="body", so Bootstrap floats them next to the icon, independent of #header) are
        // all plain Bootstrap widgets with no VDI awareness, so Omnissa's overlay paints over them same
        // as it did the header/footer. Rather than wiring a clip rect into every one of these
        // individually, delegate on Bootstrap's own shown/hidden events (fired for every dropdown-menu,
        // popover, and tooltip in the app) and clip whichever one is currently open - this covers all of
        // them, including any added later, for free.
        function enablePopupClipMode() {
            if (popupClipModeEnabled) {
                return;
            }
            popupClipModeEnabled = true;

            $window
                .$(document)
                .on('shown.bs.dropdown.vdiPopupClip', onDropdownShown)
                .on('hidden.bs.dropdown.vdiPopupClip', onDropdownHidden)
                .on('shown.bs.popover.vdiPopupClip', onPopoverShown)
                .on('hidden.bs.popover.vdiPopupClip', onPopoverHidden)
                .on('shown.bs.tooltip.vdiPopupClip', onTooltipShown)
                .on('hidden.bs.tooltip.vdiPopupClip', onTooltipHidden);

            syncAlreadyOpenPopups();
        }

        // Handles the video-becomes-active transition happening while a widget is already open (e.g. a
        // device dropdown left open through a mute/unmute that toggles video presence) - the shown/hidden
        // handlers above only see the *next* show, not one already in progress.
        function syncAlreadyOpenPopups() {
            $window
                .$('.dropdown-menu')
                .parent('.open')
                .each(function () {
                    var container = $window.$(this);
                    scheduleClipRect(container, function () {
                        return container.find('.dropdown-menu').get(0);
                    });
                });

            // Bootstrap sets aria-describedby on the trigger (and removes it on hide) for both popovers
            // and tooltips - see tip()/hide() in bootstrap's tooltip.js - so this covers both kinds
            // regardless of whether they were declared via data-toggle or shown programmatically.
            $window.$('[aria-describedby]').each(function () {
                var trigger = $window.$(this);
                var tip = document.getElementById(trigger.attr('aria-describedby'));
                if (tip) {
                    scheduleClipRect(trigger, function () {
                        return tip;
                    });
                }
            });
        }

        function disablePopupClipMode() {
            if (!popupClipModeEnabled) {
                return;
            }
            popupClipModeEnabled = false;

            $window.$(document).off('.vdiPopupClip');
            // .slice() - removeEntry splices openPopupClipRects, which would skip entries if iterated live.
            openPopupClipRects.slice().forEach(removeEntry);
        }

        function onDropdownShown(event) {
            var container = $window.$(event.target);
            scheduleClipRect(container, function () {
                return container.find('.dropdown-menu').get(0);
            });
        }

        function onDropdownHidden(event) {
            cancelOrRemoveClipRect($window.$(event.target));
        }

        function onPopoverShown(event) {
            var trigger = $window.$(event.target);
            scheduleClipRect(trigger, function () {
                var popoverInstance = trigger.data('bs.popover');
                return popoverInstance && popoverInstance.tip && popoverInstance.tip().get(0);
            });
        }

        function onPopoverHidden(event) {
            cancelOrRemoveClipRect($window.$(event.target));
        }

        function onTooltipShown(event) {
            var trigger = $window.$(event.target);
            scheduleClipRect(trigger, function () {
                var tooltipInstance = trigger.data('bs.tooltip');
                return tooltipInstance && tooltipInstance.tip && tooltipInstance.tip().get(0);
            });
        }

        function onTooltipHidden(event) {
            cancelOrRemoveClipRect($window.$(event.target));
        }

        function scheduleClipRect(element, resolveClippedElement) {
            var entry = {
                element: element.get(0),
                rect: null,
                cancelTransitionWait: null,
                intervalPromise: null,
                mutationObserver: null,
                mutationDebouncePromise: null
            };
            // Bootstrap's 'shown' event fires once its own show/hide classList toggle is done, but
            // dropdown.less/tooltip's own opacity/transform transition (~250ms) can still be running past
            // that point - waiting a fixed guess (as before) meant a slower-than-usual transition (e.g.
            // under load) still got measured mid-transition and ended up with no clip at all, same class of
            // bug as the header/footer one above. Wait for the clipped element's real transitionend instead,
            // with the old fixed duration kept only as the fallback if it never fires.
            entry.cancelTransitionWait = waitForElementTransition(
                resolveClippedElement(),
                POPUP_TRANSITION_MS,
                function () {
                    entry.cancelTransitionWait = null;
                    entry.intervalPromise = $interval(function () {
                        updateClipRect(entry, resolveClippedElement);
                    }, POPUP_RESIZE_POLL_MS);
                    updateClipRect(entry, resolveClippedElement);
                }
            );
            openPopupClipRects.push(entry);
        }

        function updateClipRect(entry, resolveClippedElement) {
            if (!document.contains(entry.element)) {
                // Trigger element was removed without Bootstrap's hide() firing (e.g. ng-if reacting to
                // a device disappearing while its dropdown was open) - clean up instead of leaving an
                // orphaned clip rect + interval running for the rest of the meeting.
                removeEntry(entry);
                return;
            }

            var clippedElement = resolveClippedElement();
            if (clippedElement && !entry.mutationObserver) {
                attachMutationObserver(entry, clippedElement, resolveClippedElement);
            }

            var rect = clippedElement && clippedElement.getBoundingClientRect();
            if (!rect || rect.width <= 0 || rect.height <= 0) {
                return;
            }
            var insetRect = shrinkRect(rect, POPUP_CLIP_INSET_PX);
            if (entry.rect && rectsEqual(entry.rect, insetRect)) {
                return;
            }
            if (entry.rect) {
                removeClipRectSafe(entry.rect);
            }
            addClipRectSafe(insetRect);
            entry.rect = insetRect;
        }

        // Reacts to the dropdown/popover's own content changing (e.g. device rows arriving as
        // enumeration finishes) far faster than the POPUP_RESIZE_POLL_MS fallback above.
        function attachMutationObserver(entry, clippedElement, resolveClippedElement) {
            if (typeof MutationObserver === 'undefined') {
                return;
            }
            entry.mutationObserver = new MutationObserver(function () {
                $timeout.cancel(entry.mutationDebouncePromise);
                entry.mutationDebouncePromise = $timeout(function () {
                    updateClipRect(entry, resolveClippedElement);
                }, POPUP_MUTATION_DEBOUNCE_MS);
            });
            entry.mutationObserver.observe(clippedElement, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['style', 'class']
            });
        }

        // Confirmed via live testing (console-injected clip rects at various insets): 2px on every side
        // clears the black gap at the dropdown/popover's rounded corners (dropdown.less: border-radius:
        // 3px) without needing a border-radius/border-width calculation per element.
        function shrinkRect(rect, insetPx) {
            var shrunk = new DOMRect();
            shrunk.x = rect.x + insetPx;
            shrunk.y = rect.y + insetPx;
            shrunk.width = Math.max(0, rect.width - insetPx * 2);
            shrunk.height = Math.max(0, rect.height - insetPx * 2);
            return shrunk;
        }

        function rectsEqual(a, b) {
            return a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height;
        }

        // Handles both cases: the popup closed before its delayed rect capture ran (cancel it), and the
        // popup closed after (remove the clip rect that was added).
        function cancelOrRemoveClipRect(element) {
            var domElement = element.get(0);
            var entry = openPopupClipRects.find(function (candidate) {
                return candidate.element === domElement;
            });
            if (entry) {
                removeEntry(entry);
            }
        }

        function removeEntry(entry) {
            var index = openPopupClipRects.indexOf(entry);
            if (index >= 0) {
                openPopupClipRects.splice(index, 1);
            }
            if (entry.cancelTransitionWait) {
                entry.cancelTransitionWait();
            }
            $timeout.cancel(entry.mutationDebouncePromise);
            $interval.cancel(entry.intervalPromise);
            if (entry.mutationObserver) {
                entry.mutationObserver.disconnect();
            }
            if (entry.rect) {
                removeClipRectSafe(entry.rect);
            }
        }

        function setAnnotationButtonBounds(newBounds) {
            annotationButtonBounds = newBounds;
        }

        function onVdiToggleAnnotationUi(e, hideButton) {
            if (annotationButtonBounds) {
                if (hideButton) {
                    removeClipRectSafe(annotationButtonBounds);
                } else {
                    $timeout(() => {
                        addClipRectSafe(annotationButtonBounds);
                    }, 800);
                }
            }
        }

        function onVdiReshowAnnotationUi() {
            if (annotationButtonBounds) {
                $window.$('#annotation-button').addClass('hide-pen');
                $timeout(() => {
                    if ($window.$('#annotation-button').hasClass('hide-pen')) {
                        $window.$('#annotation-button').removeClass('hide-pen');
                    }
                    addClipRectSafe(annotationButtonBounds);
                }, 1000);
            }
        }
    }
]);
